import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { resourcesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import { STATUS_OPTIONS } from '../../constants/listOptions';
import ConfirmDialog from '../../components/ConfirmDialog';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatBRL } from '../../lib/utils';

function ResourcesList() {
    usePageTitle('Recursos');
    const navigate = useNavigate();
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canResources = can('Resources');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

    // Carrega dados iniciais
    const list = useTableList({
        storageKey: 'resources',
        fetcher: (params) => resourcesService.list(params),
    });

    const handleNewResource = () => {
        navigate('/production/resources/new');
    };

    const handleEditResource = (resource) => {
        navigate(`/production/resources/${resource.id}/edit`);
    };

    const handleDeleteClick = (resource) => {
        setDeleteDialog({ open: true, id: resource.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await resourcesService.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Recurso excluído',
                description: 'O recurso foi excluído com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error deleting resource:', error);
            toastApiError(error, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o recurso' });
        }
    };


    const getStatusBadge = (status) => {
        const statusMap = {
            active: { label: 'Ativo', variant: 'success' },
            inactive: { label: 'Inativo', variant: 'secondary' },
        };
        const config = statusMap[status] || { label: status, variant: 'default' };
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const columns = [
        {
            key: 'code',
            label: 'Código',
            className: 'font-medium',
        },
        {
            key: 'description',
            label: 'Descrição',
        },
        {
            key: 'unit',
            label: 'Unidade',
        },
        {
            key: 'unit_cost',
            label: 'Custo Unitário',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => formatBRL(value),
        },
        {
            key: 'operators_count',
            label: 'Operadores',
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => getStatusBadge(value),
        },
    ];

    const filters = [
        { key: 'code', label: 'Código', type: 'text' },
        { key: 'description', label: 'Descrição', type: 'text' },
        { key: 'unit', label: 'Unidade', type: 'text' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
        { key: 'cost_min', label: 'Custo unitário mínimo', type: 'number' },
        { key: 'cost_max', label: 'Custo unitário máximo', type: 'number' },
    ];

    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title="Recursos"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/resources' },
                    ]}
                    actions={[
                        ...(canResources ? [{
                            label: 'Novo Recurso',
                            icon: 'new',
                            as: Link,
                            to: '/production/resources/new',
                        }] : []),
                    ]}
                />

                {/* Table */}
                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por código ou descrição..."
                    columns={columns}
                    editTo={canResources ? (row) => `/production/resources/${row.id}/edit` : undefined}
                    onDelete={canResources ? handleDeleteClick : undefined}
                    emptyMessage="Nenhum recurso cadastrado"
                />
            </div>


            {/* Confirm Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir Recurso"
                message="Tem certeza que deseja excluir este recurso? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />
        </AppLayout>
    );
}

export default ResourcesList;

